import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { CheckboxCard } from "../../../../forms/forms";
import {
  AssociationType,
  AssociatedItem,
  AttachmentViewModel,
  InventoryItem,
  InventoryService,
  CatalogService,
} from "../../../inventory";
import { ToastrService } from "ngx-toastr";
import { AttachmentSuggestion, OpenAIService } from "../../../../rubbl-shared/services/open-ai.service";
import { AzureSearchService, AzureSearchRequest } from "../../../../azure-search/azure-search";
import { deepClone } from "fast-json-patch";
import { defer, EMPTY, forkJoin, Observable, of, throwError } from "rxjs";
import { mergeMap, catchError, map, tap } from "rxjs/operators";

interface RequiredSpecInput {
  name: string;
  value: number | string | null;
  unitOfMeasure: string;
  unitOfMeasureData?: any;
  allowedValues?: string[];
  specificationType?: "Measurement" | "Enumeration";
}

interface AttachmentFormData {
  name: string;
  type: string;
  description: string;
  monthlyRate: number;
  weeklyRate: number;
  width?: string;
  catalogItemId: string;
  inventoryItemId: string;
  associationId: string;
  requiredSpecs: RequiredSpecInput[];
  deferred: boolean;
}

interface SuggestionRecord {
  /** Display / API fields (rates filled in on configuration step). */
  suggestion: AttachmentSuggestion;
  catalogItemId: string;
  associationId?: string;
  inventoryItemId?: string;
  associationIds?: string[];
  inventoryItemIds?: string[];
  quantity?: number;
  deferredCatalogData?: { src: any; catalogMatch: any; specs: any[] };
  requiredSpecs?: RequiredSpecInput[];
}

@Component({
  selector: "common-machine-create-wizard-attachments",
  templateUrl: "./machine-create-wizard-attachments.component.html",
  styleUrls: ["./machine-create-wizard-attachments.component.scss"],
})
export class MachineCreateWizardAttachmentsComponent implements OnInit {
  machineId: string;

  machine: InventoryItem | null = null;
  machineLoading: boolean = false;
  existingAttachments: any[] = [];

  attachmentCheckboxCards: CheckboxCard[] = [];
  suggestionsLoading: boolean = true;

  currentStep: "attachments" | "configuration" = "attachments";

  attachmentCatalogItems: any[] = [];

  attachments: AttachmentFormData[] = [];
  loading: boolean = false;

  /** Keyed by attachment catalog item id (checkbox card value). */
  private suggestionMap = new Map<string, SuggestionRecord>();
  private typeDefPrimarySpecMap = new Map<string, string>();
  pendingOperations = new Set<string>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private inventoryService: InventoryService,
    private catalogService: CatalogService,
    private azureSearchService: AzureSearchService,
    private openAIService: OpenAIService,
  ) {}

  ngOnInit(): void {
    this.loadMachineFromParams();
  }

  private loadMachineFromParams(): void {
    const machineId = this.route.snapshot.params["id"];
    const partitionKey = this.route.snapshot.queryParams["partitionKey"];

    if (machineId && partitionKey) {
      this.loadMachine(machineId, partitionKey);
    } else {
      this.toastrService.error("Machine ID or partition key not found. Please try creating the machine again.");
      this.router.navigate(["/owner/fleet"]);
    }
  }

  private loadMachine(machineId: string, partitionKey: string): void {
    this.machineLoading = true;

    forkJoin({
      machine: this.inventoryService.getWithPartitionKey(machineId, partitionKey),
      attachments: this.inventoryService.getAttachments(machineId).pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ machine, attachments }) => {
        this.machine = machine as InventoryItem;
        this.machineId = machineId;
        this.existingAttachments = attachments || [];
        this.machineLoading = false;

        this.loadAttachmentCatalogItems();
      },
      error: () => {
        this.toastrService.error("Failed to load machine details. Please try again.");
        this.machineLoading = false;
        this.router.navigate(["/owner/fleet"]);
      },
    });
  }

  private loadAttachmentCatalogItems(): void {
    this.suggestionsLoading = true;
    this.attachmentCatalogItems = [];

    if (!this.machine?.typeDefinitionId) {
      this.suggestionsLoading = false;
      this.toastrService.warning(
        "Machine type is missing. Complete machine details first, then return to select attachments.",
      );
      this.processCatalogSelections().subscribe();
      return;
    }

    const machineTypeId = this.machine.typeDefinitionId;
    const typeDefFilter =
      `definitionType eq 'AttachmentType' and status eq 'Published' and compatibleWith/any(c: c/id eq '${machineTypeId}')`;

    const typeDefSearch = new AzureSearchRequest();
    typeDefSearch.top = 10000;
    typeDefSearch.select = "code";
    typeDefSearch.filter = typeDefFilter;

    this.azureSearchService.search<any>(typeDefSearch, "type-definitions").pipe(
      mergeMap(typeDefResult => {
        const compatibleAttachmentTypes = (typeDefResult?.value || [])
          .map((x: any) => x.code)
          .filter((c: string) => !!c);
        const catalogFilter = `status eq 'Published' and definitionType eq 'AttachmentType'`;
        const typeCodeClause = compatibleAttachmentTypes.length
          ? `search.in(typeDefinitionCode, '${compatibleAttachmentTypes.join(",")}', ',')`
          : "";
        const compatWithClause = this.machine.catalogItemId
          ? `compatibleWith/any(c: c/id eq '${this.machine.catalogItemId}')`
          : "";
        if (!typeCodeClause && !compatWithClause) {
          return of({ value: [] });
        }
        const catalogClause =
          typeCodeClause && compatWithClause
            ? `(${typeCodeClause} or ${compatWithClause})`
            : typeCodeClause || `(${compatWithClause})`;
        const catalogSearch = new AzureSearchRequest();
        catalogSearch.top = 10000;
        catalogSearch.filter = `${catalogFilter} and ${catalogClause}`;

        return this.azureSearchService.search<any>(catalogSearch, "catalog").pipe(
          catchError(() => {
            this.toastrService.error("Failed to load compatible attachments.");
            return of({ value: [] });
          }),
        );
      }),
      catchError(() => {
        this.toastrService.error("Failed to load attachment types.");
        return of({ value: [] });
      }),
    ).subscribe(catalogResult => {
      this.attachmentCatalogItems = catalogResult?.value ?? [];
      this.suggestionsLoading = false;
      this.processCatalogSelections().subscribe();
    });
  }

  /** Build checkbox cards from index-loaded `attachmentCatalogItems` (keyed by catalog id). */
  private processCatalogSelections(): Observable<void> {
    this.suggestionMap.clear();

    const existingByCatalogId = new Map<
      string,
      { associationIds: string[]; inventoryItemIds: string[]; attachment?: any }
    >();
    this.existingAttachments.forEach((a: any) => {
      const catalogId = a.attachment?.catalogItemId || a.catalogItemId;
      if (catalogId) {
        const existing = existingByCatalogId.get(catalogId);
        const associationId = a.id;
        const inventoryItemId = a.attachment?.id || a.childItemId;
        if (existing) {
          if (associationId) existing.associationIds.push(associationId);
          if (inventoryItemId) existing.inventoryItemIds.push(inventoryItemId);
          if (!existing.attachment && (a.attachment || a)) existing.attachment = a.attachment || a;
        } else {
          existingByCatalogId.set(catalogId, {
            associationIds: associationId ? [associationId] : [],
            inventoryItemIds: inventoryItemId ? [inventoryItemId] : [],
            attachment: a.attachment || a,
          });
        }
      }
    });

    const fetchCalls: Observable<void>[] = [];

    const sorted = [...this.attachmentCatalogItems].sort((a: any, b: any) => {
      const baseA = this.getBaseTypeForSort(a.typeDefinition);
      const baseB = this.getBaseTypeForSort(b.typeDefinition);
      if (baseA !== baseB) return baseA.localeCompare(baseB);
      return (a.displayName || "").localeCompare(b.displayName || "");
    });

    this.attachmentCheckboxCards = sorted.map((catalogMatch: any) => {
      const catalogId = catalogMatch.id as string;
      const suggestion: AttachmentSuggestion = {
        name: (catalogMatch.displayName || catalogMatch.typeDefinition || "Attachment").trim(),
        type: catalogMatch.typeDefinition || "",
        description: "",
        monthlyRate: 0,
        weeklyRate: 0,
        dailyRate: 0,
        confidence: 1,
      };

      const existingRecord = existingByCatalogId.get(catalogId);
      const isExisting = !!existingRecord;

      const record: SuggestionRecord = { suggestion, catalogItemId: catalogId };

      if (isExisting && existingRecord) {
        record.associationId = existingRecord.associationIds[0];
        record.inventoryItemId = existingRecord.inventoryItemIds[0];
        record.associationIds = existingRecord.associationIds;
        record.inventoryItemIds = existingRecord.inventoryItemIds;
        record.quantity = Math.min(10, Math.max(1, existingRecord.associationIds.length || 1));
        existingByCatalogId.delete(catalogId);
        fetchCalls.push(
          forkJoin({
            catalogItem: this.catalogService.get(catalogId).pipe(catchError(() => of(null))),
            inventoryItem: record.inventoryItemId && this.machine?.accountId
              ? this.inventoryService.getWithPartitionKey(record.inventoryItemId, this.machine.accountId).pipe(catchError(() => of(null)))
              : of(null),
          }).pipe(
            map(({ catalogItem, inventoryItem }) => {
              const src = catalogItem || catalogMatch;
              const specs = src.specifications || [];
              const fullInventoryItem = inventoryItem || existingRecord.attachment;
              const requiredSpecs = this.getRequiredSpecs(
                specs,
                src,
                catalogMatch,
                fullInventoryItem,
              );
              if (requiredSpecs.length > 0) {
                record.requiredSpecs = requiredSpecs;
              }
            }),
          ),
        );
      }

      this.suggestionMap.set(catalogId, record);

      return {
        value: catalogId,
        label: suggestion.name,
        checked: isExisting,
        body: suggestion.description,
      };
    });

    return fetchCalls.length ? forkJoin(fetchCalls).pipe(map(() => undefined)) : of(undefined);
  }

  onSingleAttachmentCheckboxChange(cardValue: string, checkboxCards: CheckboxCard[]): void {
    const updatedCard = checkboxCards[0];
    const cardIndex = this.attachmentCheckboxCards.findIndex(card => card.value === cardValue);
    if (cardIndex === -1) return;

    this.attachmentCheckboxCards[cardIndex] = updatedCard;

    if (this.pendingOperations.has(cardValue)) return;

    if (updatedCard.checked) {
      this.createAttachment(cardValue);
    } else {
      this.deleteAttachment(cardValue);
    }
  }

  private createAttachment(catalogItemId: string): void {
    const record = this.suggestionMap.get(catalogItemId);
    const suggestion = record?.suggestion;
    if (!suggestion) {
      this.toastrService.error("Unable to add this attachment. No matching catalog item found.");
      this.revertCheckbox(catalogItemId, false);
      return;
    }

    const catalogMatch = this.attachmentCatalogItems.find((c: any) => c.id === catalogItemId);

    if (!catalogMatch) {
      console.warn(
        `No catalog match for "${suggestion.name}" (catalogId="${catalogItemId}").`,
        this.attachmentCatalogItems.map((c: any) => c.id),
      );
      this.toastrService.error(`No catalog match for "${suggestion.name}".`);
      this.revertCheckbox(catalogItemId, false);
      return;
    }

    this.pendingOperations.add(catalogItemId);

    this.catalogService.get(catalogMatch.id).pipe(
      catchError(() => of(null)),
    ).subscribe({
      next: (fullCatalogItem: any) => {
        const src = fullCatalogItem || catalogMatch;
        const specs = src.specifications || [];

        const requiredSpecs = this.getRequiredSpecs(specs, src, catalogMatch);

        if (requiredSpecs.length > 0) {
          this.pendingOperations.delete(catalogItemId);
          this.suggestionMap.set(catalogItemId, {
            suggestion,
            catalogItemId: catalogMatch.id,
            deferredCatalogData: { src, catalogMatch, specs },
            requiredSpecs,
          });
          return;
        }

        this.createAttachmentImmediately(catalogItemId, suggestion, src, catalogMatch, specs);
      },
      error: () => {
        this.pendingOperations.delete(catalogItemId);
        this.toastrService.error(`Failed to load catalog data for "${suggestion.name}".`);
        this.revertCheckbox(catalogItemId, false);
      },
    });
  }

  private createAttachmentImmediately(
    catalogItemId: string,
    suggestion: AttachmentSuggestion,
    src: any,
    catalogMatch: any,
    specs: any[],
  ): void {
    const typeDefinitionId = src.typeDefinitionId || catalogMatch.typeDefinitionId || "";

    const primarySpec = specs.find((s: any) => s.isPrimary === true) || null;

    const rates = this.harmonizeAttachmentRentalRates(suggestion.monthlyRate || 0, suggestion.weeklyRate || 0);

    const attachmentItem: any = {
      inventoryItemType: "Attachment",
      status: "Available",
      accountId: this.machine.accountId,
      location: this.machine.location,
      displayName: suggestion.name,
      make: src.make || "",
      model: src.model || "",
      description: suggestion.description,
      rentalRate: rates.monthlyRate,
      rentalRateWeekly: rates.weeklyRate,
      partitionKey: this.machine.accountId,
      catalogItemId: src.id || catalogMatch.id,
      typeDefinitionId,
      typeDefinitionName: src.typeDefinition || src.typeDefinitionName || catalogMatch.typeDefinition || "",
      specifications: specs,
      primaryMeasurement: primarySpec,
    };

    this.inventoryService.create({ item: attachmentItem, dimensions: {} }).pipe(
      mergeMap((result: any) => {
        return this.inventoryService.addAttachment(
          this.machineId,
          this.buildMachineAttachmentAssociation(result.id, rates.monthlyRate, rates.weeklyRate),
        ).pipe(
          map((assocResult: any) => ({
            catalogItemId: catalogMatch.id,
            inventoryItemId: result.id,
            associationId: assocResult.id,
          })),
        );
      }),
    ).subscribe({
      next: (created: { catalogItemId: string; inventoryItemId: string; associationId: string }) => {
        this.pendingOperations.delete(catalogItemId);
        const prev = this.suggestionMap.get(catalogItemId);
        this.suggestionMap.set(catalogItemId, {
          suggestion,
          catalogItemId: created.catalogItemId,
          associationId: created.associationId,
          inventoryItemId: created.inventoryItemId,
          requiredSpecs: prev?.requiredSpecs,
        });
      },
      error: (err) => {
        this.pendingOperations.delete(catalogItemId);
        const requiredSpecNames = this.parsePrimaryMeasurementErrors(err);
        if (requiredSpecNames.length) {
          const requiredSpecs = requiredSpecNames.map((specName: string) => {
            const existingSpec = (specs || []).find(
              (s: any) => (s?.name || "").toLowerCase() === specName.toLowerCase(),
            );
            return {
              name: specName,
              value: null,
              unitOfMeasure: existingSpec ? this.resolveMeasurementUnitDisplay(existingSpec) : "",
              unitOfMeasureData: existingSpec ? this.resolveMeasurementUnitObject(existingSpec) : null,
              specificationType: existingSpec?.specificationType,
            };
          });
          this.suggestionMap.set(catalogItemId, {
            suggestion,
            catalogItemId: catalogMatch.id,
            deferredCatalogData: { src, catalogMatch, specs },
            requiredSpecs,
          });
          return;
        }
        this.toastrService.error(`Failed to create "${suggestion.name}".`);
        this.revertCheckbox(catalogItemId, false);
      },
    });
  }

  private parsePrimaryMeasurementErrors(err: any): string[] {
    let body = err?.error;
    if (typeof body === "string") {
      try { body = JSON.parse(body); } catch { return []; }
    }
    if (!body?.PrimaryMeasurement || !Array.isArray(body.PrimaryMeasurement)) return [];

    const names = new Set<string>();
    for (const msg of body.PrimaryMeasurement) {
      if (typeof msg !== "string") continue;
      const match = msg.match(/name\s+(.+?)\s+for/i);
      if (match?.[1]) {
        const cleaned = match[1].trim().replace(/^["']|["']$/g, "");
        if (cleaned) names.add(cleaned);
      }
    }
    return Array.from(names);
  }

  private deleteAttachment(suggestionName: string): void {
    const record = this.suggestionMap.get(suggestionName);

    if (record?.deferredCatalogData) {
      record.deferredCatalogData = undefined;
      record.requiredSpecs = undefined;
      return;
    }

    const allAssociationIds = record?.associationIds?.length
      ? record.associationIds
      : (record?.associationId ? [record.associationId] : []);

    if (allAssociationIds.length === 0) {
      return;
    }

    const deleteNext = (idx: number) => {
      if (idx >= allAssociationIds.length) return;
      const isLast = idx === allAssociationIds.length - 1;
      this.performDeleteAttachment(suggestionName, allAssociationIds[idx], {
        onSuccess: () => deleteNext(idx + 1),
        revertOnError: idx === 0,
        isLastInChain: isLast,
      });
    };
    deleteNext(0);
  }

  private performDeleteAttachment(
    name: string,
    associationId: string,
    options?: { onSuccess?: () => void; revertOnError?: boolean; isLastInChain?: boolean },
  ): void {
    this.pendingOperations.add(name);

    this.inventoryService.deleteAttachment(this.machineId, associationId, this.machine.accountId).subscribe({
      next: () => {
        this.pendingOperations.delete(name);

        // Only clear record and uncheck when all associations in the chain are deleted.
        if (options?.isLastInChain) {
          const record = this.suggestionMap.get(name);
          if (record) {
            record.associationId = undefined;
            record.inventoryItemId = undefined;
            record.associationIds = undefined;
            record.inventoryItemIds = undefined;
          }

          const cardIndex = this.attachmentCheckboxCards.findIndex(c => c.value === name);
          if (cardIndex !== -1) {
            this.attachmentCheckboxCards = this.attachmentCheckboxCards.map((c, i) =>
              i === cardIndex ? { ...c, checked: false } : c
            );
          }
        }

        options?.onSuccess?.();
      },
      error: () => {
        this.pendingOperations.delete(name);
        this.toastrService.error(`Failed to remove "${this.attachmentLabel(name)}".`);

        if (options?.revertOnError) {
          this.revertCheckbox(name, true);
        }
      },
    });
  }

  private getBaseTypeForSort(type: string): string {
    const t = (type || "").trim();
    if (!t) return "";
    const parts = t.split(/\s+/);
    return (parts[parts.length - 1] || t).toLowerCase();
  }

  private revertCheckbox(suggestionName: string, checked: boolean): void {
    const cardIndex = this.attachmentCheckboxCards.findIndex(c => c.value === suggestionName);
    if (cardIndex !== -1) {
      this.attachmentCheckboxCards = this.attachmentCheckboxCards.map((c, i) =>
        i === cardIndex ? { ...c, checked } : c
      );
    }
  }

  /** Catalog often sends 0 as a placeholder; treat as empty until the user enters a value. */
  private normalizeCatalogMeasurementDefault(raw: any): number | string | null {
    if (raw == null || raw === "") return null;
    const num = Number(raw);
    if (!Number.isNaN(num) && num === 0) return null;
    if (!Number.isNaN(num)) return num;
    return typeof raw === "string" ? raw : null;
  }

  private resolveMeasurementUnitObject(spec: any): any {
    return (
      spec?.standardMeasurement?.unitOfMeasure ??
      spec?.metricMeasurement?.unitOfMeasure ??
      spec?.unitOfMeasure ??
      null
    );
  }

  private resolveMeasurementUnitDisplay(spec: any): string {
    const uom = this.resolveMeasurementUnitObject(spec);
    if (!uom) return "";
    const abbr =
      uom.primaryAbbreviation ??
      (uom as any).abbreviation ??
      (Array.isArray((uom as any).abbreviations) && (uom as any).abbreviations.length
        ? (uom as any).abbreviations[0]
        : null);
    if (abbr != null && String(abbr).trim() !== "") return String(abbr).trim();
    if (uom.name) return String(uom.name).trim();
    return "";
  }

  private getRequiredSpecs(
    specs: any[],
    src: any,
    catalogMatch: any,
    existingInventoryItem?: any,
  ): RequiredSpecInput[] {
    const required: RequiredSpecInput[] = [];
    const seen = new Set<string>();

    const typeDefId = src.typeDefinitionId || catalogMatch.typeDefinitionId;
    const primarySpecName =
      src.primarySpecification ||
      catalogMatch.primarySpecification ||
      (typeDefId ? this.typeDefPrimarySpecMap.get(typeDefId) : undefined);

    const getExistingValue = (specName: string): number | string | null => {
      if (!existingInventoryItem) return null;
      const invSpecs = existingInventoryItem.specifications || [];
      const invSpec = invSpecs.find((s: any) => s.name === specName);
      const val = invSpec?.standardMeasurement?.value ?? invSpec?.value;
      if (val == null) return null;
      const num = Number(val);
      return Number.isNaN(num) ? (typeof val === "string" ? val : null) : num;
    };

    const pushMeasurementSpec = (spec: any, value: number | string | null) => {
      const uom = this.resolveMeasurementUnitObject(spec);
      required.push({
        name: spec.name,
        value,
        unitOfMeasure: this.resolveMeasurementUnitDisplay(spec),
        unitOfMeasureData: uom,
        allowedValues: spec.allowedValues,
        specificationType: spec.specificationType,
      });
    };

    const pushEnumerationSpec = (spec: any, value: number | string | null) => {
      required.push({
        name: spec.name,
        value,
        unitOfMeasure: "",
        unitOfMeasureData: null,
        allowedValues: spec.allowedValues || spec.AllowedValues || [],
        specificationType: "Enumeration",
      });
    };

    for (const spec of specs) {
      if (!(spec.isPrimary || spec.isRequired) || seen.has(spec.name)) continue;

      if (spec.specificationType === "Measurement") {
        seen.add(spec.name);
        const existingVal = getExistingValue(spec.name);
        const catalogVal = spec.standardMeasurement?.value ?? spec.value;
        const value =
          existingVal != null && existingVal !== ""
            ? (typeof existingVal === "number" && Number.isNaN(existingVal) ? null : existingVal)
            : this.normalizeCatalogMeasurementDefault(catalogVal);
        pushMeasurementSpec(spec, typeof value === "number" && Number.isNaN(value) ? null : value);
      } else if (spec.specificationType === "Enumeration") {
        seen.add(spec.name);
        const existingVal = getExistingValue(spec.name);
        const catalogVal = spec.value;
        const value = existingVal ?? catalogVal ?? null;
        pushEnumerationSpec(spec, value);
      }
    }

    if (primarySpecName && !seen.has(primarySpecName)) {
      const existing = specs.find((s: any) => s.name === primarySpecName);
      const existingVal = getExistingValue(primarySpecName);
      const catalogVal = existing?.standardMeasurement?.value ?? existing?.value;
      const value =
        existingVal != null && existingVal !== ""
          ? (typeof existingVal === "number" && Number.isNaN(existingVal) ? null : existingVal)
          : this.normalizeCatalogMeasurementDefault(catalogVal);

      if (existing?.specificationType === "Enumeration") {
        pushEnumerationSpec(existing, typeof value === "number" && Number.isNaN(value) ? null : value);
      } else {
        const specForRow = existing || {
          name: primarySpecName,
          specificationType: "Measurement",
        };
        pushMeasurementSpec(
          specForRow,
          typeof value === "number" && Number.isNaN(value) ? null : value,
        );
      }
    }

    if (existingInventoryItem) {
      const invSpecs = existingInventoryItem.specifications || [];
      for (const invSpec of invSpecs) {
        if (!(invSpec.isPrimary || invSpec.isRequired) || seen.has(invSpec.name)) continue;

        if (invSpec.specificationType === "Measurement") {
          seen.add(invSpec.name);
          const val = invSpec.standardMeasurement?.value ?? invSpec.value;
          const numVal = val != null && !Number.isNaN(Number(val)) ? Number(val) : null;
          pushMeasurementSpec(invSpec, numVal);
        } else if (invSpec.specificationType === "Enumeration") {
          seen.add(invSpec.name);
          const val = invSpec.value ?? invSpec.standardMeasurement?.value;
          pushEnumerationSpec(invSpec, val ?? null);
        }
      }
    }

    return required;
  }

  private applyRequiredSpecsToSpecList(requiredSpecs: RequiredSpecInput[], specs: any[]): void {
    for (const reqSpec of requiredSpecs) {
      const existing = specs.find((s: any) => s.name === reqSpec.name);
      const isEnumeration = reqSpec.specificationType === "Enumeration" || existing?.specificationType === "Enumeration";

      if (existing) {
        if (isEnumeration) {
          existing.value = reqSpec.value;
        } else {
          existing.standardMeasurement = {
            value: reqSpec.value,
            unitOfMeasure: reqSpec.unitOfMeasureData || { name: reqSpec.unitOfMeasure },
          };
        }
        if (existing.isPrimary == null) existing.isPrimary = true;
      } else {
        if (isEnumeration) {
          specs.push({
            specificationType: "Enumeration",
            name: reqSpec.name,
            isPrimary: true,
            isRequired: true,
            isTypeDefinitionSpec: true,
            isPublic: true,
            value: reqSpec.value,
            allowedValues: reqSpec.allowedValues,
          });
        } else {
          specs.push({
            specificationType: "Measurement",
            name: reqSpec.name,
            isPrimary: true,
            isRequired: true,
            isTypeDefinitionSpec: true,
            isPublic: true,
            modified: false,
            replaced: false,
            standardMeasurement: {
              value: reqSpec.value,
              unitOfMeasure: reqSpec.unitOfMeasureData || { name: reqSpec.unitOfMeasure },
            },
          });
        }
      }
    }
  }

  private createDeferredAttachment(att: AttachmentFormData): Observable<any> {
    const record = this.suggestionMap.get(att.catalogItemId);
    if (!record?.deferredCatalogData) return of(null);

    const { src, catalogMatch, specs } = record.deferredCatalogData;
    const specsCopy = deepClone(specs);

    this.applyRequiredSpecsToSpecList(att.requiredSpecs, specsCopy);

    const primaryMeasurement = specsCopy.find((s: any) => s.isPrimary === true) || null;
    const typeDefinitionId = src.typeDefinitionId || catalogMatch.typeDefinitionId || "";

    const rates = this.harmonizeAttachmentRentalRates(att.monthlyRate || 0, att.weeklyRate || 0);

    const attachmentItem: any = {
      inventoryItemType: "Attachment",
      status: "Available",
      accountId: this.machine.accountId,
      location: this.machine.location,
      displayName: att.name,
      make: src.make || "",
      model: src.model || "",
      description: att.description,
      rentalRate: rates.monthlyRate,
      rentalRateWeekly: rates.weeklyRate,
      partitionKey: this.machine.accountId,
      catalogItemId: src.id || catalogMatch.id,
      typeDefinitionId,
      typeDefinitionName: src.typeDefinition || src.typeDefinitionName || catalogMatch.typeDefinition || "",
      specifications: specsCopy,
      primaryMeasurement,
    };

    return this.inventoryService.create({ item: attachmentItem, dimensions: {} }).pipe(
      mergeMap((result: any) => {
        return this.inventoryService.addAttachment(
          this.machineId,
          this.buildMachineAttachmentAssociation(result.id, rates.monthlyRate, rates.weeklyRate),
        ).pipe(
          map((assocResult: any) => ({ inventoryItemId: result.id, associationId: assocResult.id })),
        );
      }),
      catchError(err => {
        console.error(`Failed to create deferred attachment "${att.name}":`, err);
        return of(null);
      }),
    );
  }

  private createAdditionalAttachmentFromExisting(
    att: AttachmentFormData,
    sourceInventoryItemId: string,
  ): Observable<{ inventoryItemId: string; associationId: string } | null> {
    return this.inventoryService.getWithPartitionKey(sourceInventoryItemId, this.machine.accountId).pipe(
      mergeMap((fullItem: any) => {
        if (!fullItem) return of(null);

        const rates = this.harmonizeAttachmentRentalRates(att.monthlyRate || 0, att.weeklyRate || 0);

        const clonedItem: any = {
          ...fullItem,
          id: undefined,
          partitionKey: this.machine.accountId,
          rentalRate: rates.monthlyRate,
          rentalRateWeekly: rates.weeklyRate,
        };

        return this.inventoryService.create({ item: clonedItem, dimensions: {} }).pipe(
          mergeMap((result: any) => {
            return this.inventoryService.addAttachment(
              this.machineId,
              this.buildMachineAttachmentAssociation(result.id, rates.monthlyRate, rates.weeklyRate),
            ).pipe(
              map((assocResult: any) => ({ inventoryItemId: result.id, associationId: assocResult.id })),
            );
          }),
        );
      }),
      catchError(err => {
        console.error(`Failed to create additional attachment "${att.name}" from source ${sourceInventoryItemId}:`, err);
        return of(null);
      }),
    );
  }

  private harmonizeAttachmentRentalRates(monthlyRate: number, weeklyRate: number): { monthlyRate: number; weeklyRate: number } {
    const m = Math.max(0, Number(monthlyRate) || 0);
    const w = Math.max(0, Number(weeklyRate) || 0);
    const hasMonthly = m > 0;
    const hasWeekly = w > 0;
    const mult = MachineCreateWizardAttachmentsComponent.monthlyToWeeklyMultiplier;
    if (hasMonthly && !hasWeekly) {
      return { monthlyRate: m, weeklyRate: Math.round(m * mult * 100) / 100 };
    }
    if (!hasMonthly && hasWeekly) {
      return { monthlyRate: Math.round((w / mult) * 100) / 100, weeklyRate: w };
    }
    return { monthlyRate: m, weeklyRate: w };
  }

  private buildMachineAttachmentAssociation(childItemId: string, monthlyRate: number, weeklyRate: number): AssociatedItem {
    return {
      accountId: this.machine!.accountId,
      associationType: AssociationType.InventoryItem,
      childItemId,
      parentItemId: this.machineId,
      price: monthlyRate,
      weeklyPrice: weeklyRate,
      status: "Available",
      isFixed: false,
      automaticAttachment: false,
      inventoryItemId: this.machineId,
      buyItNowPrice: 0,
      purchasePrice: 0,
    };
  }

  private attachmentLabel(catalogItemId: string): string {
    return this.suggestionMap.get(catalogItemId)?.suggestion?.name ?? catalogItemId;
  }

  private groupAttachmentsByCatalogId(options?: { requireCatalogItemId?: boolean }): Map<string, AttachmentFormData[]> {
    const byCatalog = new Map<string, AttachmentFormData[]>();
    for (const att of this.attachments) {
      if (options?.requireCatalogItemId && !att.catalogItemId) continue;
      const list = byCatalog.get(att.catalogItemId) || [];
      list.push(att);
      byCatalog.set(att.catalogItemId, list);
    }
    return byCatalog;
  }

  private syncSuggestionMapFromAttachmentGroups(byCatalog: Map<string, AttachmentFormData[]>): void {
    for (const [catalogId, list] of byCatalog) {
      const record = this.suggestionMap.get(catalogId);
      if (!record) continue;
      const first = list[0];
      if (first?.requiredSpecs?.length) {
        record.requiredSpecs = first.requiredSpecs;
      }
      record.quantity = list.length;
      record.inventoryItemIds = list.map(a => a.inventoryItemId).filter(Boolean);
      record.associationIds = list.map(a => a.associationId).filter(Boolean);
      record.inventoryItemId = record.inventoryItemIds[0];
      record.associationId = record.associationIds[0];
    }
  }

  private static isIncompleteRequiredSpecValue(value: number | string | null | undefined): boolean {
    if (value == null || value === "") return true;
    return typeof value === "number" && isNaN(value);
  }

  /** Max instances per attachment catalog item (same as former quantity cap). */
  private static readonly maxSameCatalogAttachments = 10;

  /** Matches server `BillingConstants.DefaultWeeklyMultiplier` (monthly → weekly). */
  private static readonly monthlyToWeeklyMultiplier = 0.333333;

  canAddAnotherAttachment(catalogItemId: string): boolean {
    const n = this.attachments.filter(a => a.catalogItemId === catalogItemId).length;
    return n < MachineCreateWizardAttachmentsComponent.maxSameCatalogAttachments;
  }

  addAnotherAttachment(index: number): void {
    const src = this.attachments[index];
    if (!src || !this.canAddAnotherAttachment(src.catalogItemId)) return;
    const dup = this.cloneAttachmentRowForDuplicate(src);
    this.attachments.splice(index + 1, 0, dup);
  }

  private cloneAttachmentRowForDuplicate(src: AttachmentFormData): AttachmentFormData {
    const requiredSpecs = this.cloneRequiredSpecsTemplate(src.requiredSpecs || []);
    return {
      name: src.name,
      type: src.type,
      description: src.description,
      monthlyRate: 0,
      weeklyRate: 0,
      width: src.width,
      catalogItemId: src.catalogItemId,
      inventoryItemId: "",
      associationId: "",
      requiredSpecs,
      deferred: src.deferred,
    };
  }

  hasSelectedAttachments(): boolean {
    return this.attachmentCheckboxCards.some(card => card.checked);
  }

  hasPendingOperations(): boolean {
    return this.pendingOperations.size > 0;
  }

  hasIncompleteRequiredSpecs(): boolean {
    return this.attachments.some(att =>
      att.requiredSpecs?.some(spec => MachineCreateWizardAttachmentsComponent.isIncompleteRequiredSpecValue(spec.value)),
    );
  }

  proceedToConfiguration(): void {
    const previousByCatalogId = this.groupAttachmentsByCatalogId({ requireCatalogItemId: true });

    const rows: AttachmentFormData[] = [];

    for (const record of this.suggestionMap.values()) {
      const hasDeferred = !!record.deferredCatalogData;
      const hasExisting = !!(record.associationId && record.inventoryItemId);
      if (!hasDeferred && !hasExisting) continue;

      const prevList = previousByCatalogId.get(record.catalogItemId) || [];
      const prevFirst = prevList[0];
      const s = record.suggestion;
      const name = s?.name || "";
      const baseSpecs = prevFirst?.requiredSpecs?.length
        ? deepClone(prevFirst.requiredSpecs)
        : deepClone(record.requiredSpecs || []);

      if (hasDeferred) {
        const deferredTargetCount = Math.min(
          MachineCreateWizardAttachmentsComponent.maxSameCatalogAttachments,
          Math.max(1, Math.max(prevList.length, record.quantity ?? 1)),
        );
        for (let i = 0; i < deferredTargetCount; i++) {
          const prevRow = prevList[i];
          rows.push({
            name,
            type: s?.type || "",
            description: s?.description || "",
            ...this.configurationRowRates(prevRow, prevFirst, s),
            catalogItemId: record.catalogItemId,
            inventoryItemId: "",
            associationId: "",
            requiredSpecs: this.specsForConfigurationRow(i, prevRow, baseSpecs),
            deferred: true,
          });
        }
        continue;
      }

      const { invIds, assocIds } = this.pairedIdsFromRecord(record);
      const paired = Math.max(invIds.length, assocIds.length, 1);
      const targetCount = Math.min(
        MachineCreateWizardAttachmentsComponent.maxSameCatalogAttachments,
        Math.max(paired, record.quantity ?? paired),
      );

      for (let i = 0; i < targetCount; i++) {
        const prevRow = prevList[i];
        rows.push({
          name,
          type: s?.type || "",
          description: s?.description || "",
          ...this.configurationRowRates(prevRow, prevFirst, s),
          catalogItemId: record.catalogItemId,
          inventoryItemId: invIds[i] || "",
          associationId: assocIds[i] || "",
          requiredSpecs: this.specsForConfigurationRow(i, prevRow, baseSpecs),
          deferred: false,
        });
      }
    }

    this.attachments = rows.sort((a, b) => {
      const baseA = this.getBaseTypeForSort(a.type);
      const baseB = this.getBaseTypeForSort(b.type);
      if (baseA !== baseB) return baseA.localeCompare(baseB);
      return (a.name || "").localeCompare(b.name || "");
    });

    this.currentStep = "configuration";
    window.scrollTo(0, 0);
  }

  private cloneRequiredSpecsTemplate(specs: RequiredSpecInput[]): RequiredSpecInput[] {
    return (specs || []).map(s => ({
      ...deepClone(s),
      value: null as number | string | null,
    }));
  }

  private assignCreatedToAttachmentRow(
    row: AttachmentFormData,
    created: { inventoryItemId: string; associationId: string } | null,
  ): void {
    if (!created) return;
    row.inventoryItemId = created.inventoryItemId;
    row.associationId = created.associationId;
  }

  private configurationRowRates(
    prevRow: AttachmentFormData | undefined,
    prevFirst: AttachmentFormData | undefined,
    s: AttachmentSuggestion | undefined,
  ): Pick<AttachmentFormData, "monthlyRate" | "weeklyRate" | "width"> {
    return {
      monthlyRate: prevRow?.monthlyRate ?? prevFirst?.monthlyRate ?? s?.monthlyRate ?? 0,
      weeklyRate: prevRow?.weeklyRate ?? prevFirst?.weeklyRate ?? s?.weeklyRate ?? 0,
      width: prevRow?.width ?? prevFirst?.width ?? s?.width ?? undefined,
    };
  }

  private specsForConfigurationRow(
    index: number,
    prevRow: AttachmentFormData | undefined,
    baseSpecs: RequiredSpecInput[],
  ): RequiredSpecInput[] {
    if (prevRow?.requiredSpecs?.length) {
      return deepClone(prevRow.requiredSpecs);
    }
    return index === 0 ? baseSpecs : this.cloneRequiredSpecsTemplate(baseSpecs);
  }

  private pairedIdsFromRecord(record: SuggestionRecord): { invIds: string[]; assocIds: string[] } {
    const invIds = record.inventoryItemIds?.length
      ? [...record.inventoryItemIds]
      : record.inventoryItemId
        ? [record.inventoryItemId]
        : [];
    const assocIds = record.associationIds?.length
      ? [...record.associationIds]
      : record.associationId
        ? [record.associationId]
        : [];
    return { invIds, assocIds };
  }

  /** Creates inventory + association rows for duplicate cards that only have a catalog selection (no ids yet). */
  private ensurePendingInventoryRowsCreated(): Observable<void> {
    const pending = this.attachments.filter(
      att => !att.deferred && !att.inventoryItemId && att.catalogItemId,
    );
    if (pending.length === 0) return of(undefined);

    const missingSource = pending.filter(
      att => !this.attachments.some(a => a.catalogItemId === att.catalogItemId && !!a.inventoryItemId),
    );
    if (missingSource.length > 0) {
      return defer(() => {
        this.toastrService.error(
          missingSource.length === 1
            ? `Could not save "${this.attachmentLabel(missingSource[0].catalogItemId)}": no saved attachment to copy from. Remove the extra row or go back and select the attachment again.`
            : `${missingSource.length} attachment rows have no saved item to copy from. Remove the extra rows or go back and select the attachments again.`,
        );
        return throwError(() => new Error("No source inventory item to duplicate attachment row."));
      });
    }

    return forkJoin(
      pending.map(att => {
        const sourceId = this.attachments.find(
          a => a.catalogItemId === att.catalogItemId && !!a.inventoryItemId,
        )!.inventoryItemId;
        return this.createAdditionalAttachmentFromExisting(att, sourceId).pipe(
          tap(created => this.assignCreatedToAttachmentRow(att, created)),
        );
      }),
    ).pipe(map(() => undefined));
  }

  private reconcileAndUpdateAttachments(): Observable<Array<{ success: boolean }>> {
    return this.ensurePendingInventoryRowsCreated().pipe(
      mergeMap(() => this.updateAttachmentInventoryItems()),
      mergeMap(inventoryResults =>
        this.updateAttachmentAssociations().pipe(map(assocResults => [...inventoryResults, ...assocResults])),
      ),
    );
  }

  private updateAttachmentInventoryItems(): Observable<Array<{ success: boolean }>> {
    const itemIdsToUpdate = this.attachments.reduce(
      (acc: { att: AttachmentFormData; inventoryItemId: string }[], att) => {
        if (!att.inventoryItemId) return acc;
        return acc.concat({ att, inventoryItemId: att.inventoryItemId });
      },
      [],
    );

    const updateCalls = itemIdsToUpdate.map(({ att, inventoryItemId }) =>
      this.inventoryService.getWithPartitionKey(inventoryItemId, this.machine.accountId).pipe(
        mergeMap((fullItem: any) => {
          const rates = this.harmonizeAttachmentRentalRates(att.monthlyRate || 0, att.weeklyRate || 0);
          fullItem.rentalRate = rates.monthlyRate;
          fullItem.rentalRateWeekly = rates.weeklyRate;
          if (att.requiredSpecs?.length) {
            const specs = fullItem.specifications || [];
            this.applyRequiredSpecsToSpecList(att.requiredSpecs, specs);
            fullItem.primaryMeasurement =
              specs.find((s: any) => s.isPrimary === true) || fullItem.primaryMeasurement;
          }
          return this.inventoryService.update(inventoryItemId, fullItem).pipe(map(() => ({ success: true })));
        }),
        catchError(err => {
          console.error(`Failed to save attachment config for "${att.name}" (${inventoryItemId}):`, err);
          return of({ success: false });
        }),
      ),
    );

    return updateCalls.length ? forkJoin(updateCalls) : of([]);
  }

  private updateAttachmentAssociations(): Observable<Array<{ success: boolean }>> {
    const rows = this.attachments.filter(att => !!att.associationId && !att.deferred);
    if (rows.length === 0) return of([]);

    const calls = rows.map(att => {
      const rates = this.harmonizeAttachmentRentalRates(att.monthlyRate || 0, att.weeklyRate || 0);
      return this.inventoryService.getAttachmentAssociation(this.machineId, att.associationId, this.machine.accountId).pipe(
        mergeMap((assoc: AssociatedItem) => {
          const payload = { ...assoc, price: rates.monthlyRate, weeklyPrice: rates.weeklyRate } as AssociatedItem;
          return this.inventoryService
            .updateAttachment(this.machineId, att.associationId, payload as AttachmentViewModel)
            .pipe(map(() => ({ success: true })));
        }),
        catchError(err => {
          console.error(`Failed to update attachment association for "${att.name}" (${att.associationId}):`, err);
          return of({ success: false });
        }),
      );
    });

    return forkJoin(calls);
  }

  /**
   * After removing a row, keep suggestionMap and the selection checkbox aligned with remaining rows.
   */
  private syncCatalogRecordAfterRemoval(catalogItemId: string): void {
    const remainingSame = this.attachments.filter(a => a.catalogItemId === catalogItemId);
    const record = this.suggestionMap.get(catalogItemId);

    if (remainingSame.length === 0) {
      if (record) {
        record.associationId = undefined;
        record.inventoryItemId = undefined;
        record.associationIds = undefined;
        record.inventoryItemIds = undefined;
        record.quantity = 1;
        record.deferredCatalogData = undefined;
        record.requiredSpecs = undefined;
      }
      this.revertCheckbox(catalogItemId, false);
      return;
    }

    if (record) {
      record.inventoryItemIds = remainingSame.map(a => a.inventoryItemId).filter(Boolean);
      record.associationIds = remainingSame.map(a => a.associationId).filter(Boolean);
      record.inventoryItemId = record.inventoryItemIds[0];
      record.associationId = record.associationIds[0];
      record.quantity = remainingSame.length;
    }
  }

  private spliceAttachmentRow(row: AttachmentFormData): void {
    const idx = this.attachments.indexOf(row);
    if (idx !== -1) {
      this.attachments.splice(idx, 1);
    }
  }

  removeAttachment(index: number): void {
    const attachment = this.attachments[index];
    if (!attachment) return;

    const rowRef = attachment;
    const catalogId = attachment.catalogItemId;

    if (attachment.deferred) {
      this.spliceAttachmentRow(rowRef);
      this.syncCatalogRecordAfterRemoval(catalogId);
      return;
    }

    if (!attachment.associationId) {
      this.spliceAttachmentRow(rowRef);
      this.syncCatalogRecordAfterRemoval(catalogId);
      return;
    }

    const associationIdToRemove = attachment.associationId;
    const sourceInventoryId = attachment.inventoryItemId;

    const pendingSiblings = this.attachments.filter(
      a =>
        a.catalogItemId === catalogId &&
        !a.inventoryItemId &&
        !a.deferred &&
        a !== rowRef,
    );

    if (pendingSiblings.length > 0 && !sourceInventoryId) {
      this.toastrService.error(
        "Cannot remove this attachment while duplicate rows still need to be saved. Refresh and try again, or remove the unsaved rows first.",
      );
      return;
    }

    const materializePending$ =
      pendingSiblings.length > 0 && sourceInventoryId
        ? forkJoin(
            pendingSiblings.map(b =>
              this.createAdditionalAttachmentFromExisting(b, sourceInventoryId).pipe(
                tap(created => this.assignCreatedToAttachmentRow(b, created)),
              ),
            ),
          )
        : of(null);

    this.pendingOperations.add(attachment.catalogItemId);
    materializePending$
      .pipe(
        mergeMap((results: ({ inventoryItemId: string; associationId: string } | null)[] | null) => {
          if (
            pendingSiblings.length > 0 &&
            Array.isArray(results) &&
            results.some(r => r == null)
          ) {
            this.pendingOperations.delete(attachment.catalogItemId);
            this.toastrService.error(
              "Could not save duplicate rows before removing this attachment. Nothing was removed.",
            );
            return EMPTY;
          }
          return this.inventoryService.deleteAttachment(this.machineId, associationIdToRemove, this.machine.accountId);
        }),
      )
      .subscribe({
        next: () => {
          this.pendingOperations.delete(attachment.catalogItemId);
          this.spliceAttachmentRow(rowRef);
          this.syncCatalogRecordAfterRemoval(catalogId);
        },
        error: () => {
          this.pendingOperations.delete(attachment.catalogItemId);
          this.toastrService.error(`Failed to remove "${this.attachmentLabel(attachment.catalogItemId)}".`);
        },
      });
  }

  goBackToSelection(): void {
    if (this.loading) return;
    this.loading = true;

    this.reconcileAndUpdateAttachments().subscribe({
      next: (results: Array<{ success: boolean }>) => {
        const failedCount = results.filter(r => r && r.success === false).length;
        if (failedCount > 0) {
          this.toastrService.warning(`${failedCount} attachment config update(s) failed to save.`);
        }

        this.syncSuggestionMapFromAttachmentGroups(this.groupAttachmentsByCatalogId());
        this.currentStep = "attachments";
        this.loading = false;
        window.scrollTo(0, 0);
      },
      error: err => {
        console.error("Failed to save attachment properties on back to selection:", err);
        this.loading = false;
        this.toastrService.error("Failed to save attachment changes.");
      },
    });
  }

  onBack(): void {
    this.navigateToStep("options");
  }

  onNext(): void {
    if (this.loading) return;
    if (this.hasIncompleteRequiredSpecs()) {
      this.toastrService.warning("Please fill in all required specifications before continuing.");
      return;
    }

    this.loading = true;

    const deferred = this.attachments.filter(att => att.deferred);

    const createCalls = deferred.map(att => this.createDeferredAttachment(att));

    (deferred.length ? forkJoin(createCalls) : of([] as any[])).pipe(
      tap((createResults: ({ inventoryItemId: string; associationId: string } | null)[]) => {
        createResults.forEach((result, idx) => {
          const att = deferred[idx];
          if (!att || !result) return;
          att.inventoryItemId = result.inventoryItemId;
          att.associationId = result.associationId;
          att.deferred = false;
        });
      }),
      mergeMap((createResults: ({ inventoryItemId: string; associationId: string } | null)[]) =>
        this.reconcileAndUpdateAttachments().pipe(map(updateResults => ({ createResults, updateResults }))),
      ),
      mergeMap(({ createResults, updateResults }) => {
        const createFailed = (createResults || []).filter((r: any) => r === null).length;
        if (createFailed > 0) {
          this.toastrService.warning(`${createFailed} attachment(s) failed to create. Check required fields and try again.`);
        }
        const failedCount = updateResults.filter(r => r && r.success === false).length;
        if (failedCount > 0) {
          this.toastrService.warning(`${failedCount} attachment rate(s) failed to save.`);
        }
        return this.inventoryService.update(this.machineId, this.machine).pipe(
          mergeMap(() => this.regenerateMachineDescriptionAfterAttachments()),
        );
      }),
    ).subscribe({
      next: () => this.navigateToStep("photos"),
      error: (err) => {
        this.loading = false;
        console.error("Failed to save machine after attachment updates:", err);
        this.toastrService.error("Failed to save attachments. Please try again.");
      },
    });
  }

  private navigateToStep(step: string): void {
    this.loading = false;
    const baseUrl = this.router.url.split("/").slice(0, -1).join("/");
    this.router.navigate([baseUrl, step], {
      queryParams: { partitionKey: this.route.snapshot.queryParams["partitionKey"] },
    });
  }

  /**
   * Runs after attachments are saved. Options step no longer generates the listing description
   * because it runs before attachments; this pass includes real attachment names.
   */
  private regenerateMachineDescriptionAfterAttachments(): Observable<void> {
    const partitionKey = this.route.snapshot.queryParams["partitionKey"];
    const m = this.machine;
    if (!m || !partitionKey || !m.make || !m.model) {
      return of(undefined);
    }

    const selectedAttachments = (this.attachments || []).map(a => a.name);
    const selectedOptions = (m.options || []).map(o => o.name);
    const machineType = m.typeDefinitionName || "";

    return this.openAIService
      .generateMachineDescription(
        m.make,
        m.model,
        m.year?.toString() || "Unknown",
        machineType,
        (m as any).usage?.standard?.value || 0,
        selectedAttachments,
        selectedOptions,
      )
      .pipe(
        mergeMap(description => {
          if (!description) {
            return of(undefined);
          }
          return this.inventoryService
            .jsonPatchWithPartitionKey(
              this.machineId,
              [{ op: "replace", path: "/description", value: description }],
              partitionKey,
            )
            .pipe(
              map(() => undefined),
              catchError(() => of(undefined)),
            );
        }),
        catchError(() => of(undefined)),
      );
  }
}
